import { call, put, select, takeLatest } from "redux-saga/effects";
import { PositionDc } from "@evergis/api";
import { getCurrentFeature } from "../selectFeatures/selectors";
import { getFeatureToShow } from "../showFeature/selectors";
import { waitAction } from "../waitAction";

import {
  EditFeatureState,
  resetChangeHistory,
  resetEditFeatureChanges,
  setAttributes,
  subtractEditFeatureGeometry,
  unitEditFeatureGeometry,
  updateAttributesForNewFeature,
} from "./editFeature";

import { getCurrentEditFeature, getFirstPastEditFeature } from "./selectors";
import { Positions, setGeometryEditor } from "../geometryEditor/geometryEditor";
import { apiOperateGeometry } from "./api";
import { apiGetFeatureById, apiUpdateFeature } from "../features/api";

import { EditFeature, GeometryOperation } from "./types";
import { FeatureDcExtended } from "../selectFeatures/types";

function* operateGeometry(positions: Positions, operation: GeometryOperation) {
  const editFeature: EditFeature = yield select(getCurrentEditFeature);
  const currentNewFeature: FeatureDcExtended = yield select(getFeatureToShow);
  const currentSelectFeature: FeatureDcExtended = yield select(getCurrentFeature);
  const currentFeature = currentSelectFeature || currentNewFeature;

  if (!editFeature.geometry.length || !currentFeature) {
    return;
  }

  yield call(apiOperateGeometry, operation, positions, currentFeature);

  const currentUpdatedFeature: FeatureDcExtended = yield call(
    apiGetFeatureById,
    currentFeature.id,
    currentFeature.layer,
    currentFeature.geometry.sr,
  );

  if (!currentUpdatedFeature) {
    return;
  }

  yield put(
    setGeometryEditor({
      geometry: currentUpdatedFeature.geometry.coordinates as Positions,
    }),
  );
}

function* unitGeometry({ payload: positions }: ReturnType<typeof unitEditFeatureGeometry>) {
  yield operateGeometry(positions, "unit");
}

function* subtractGeometry({ payload: positions }: ReturnType<typeof subtractEditFeatureGeometry>) {
  yield operateGeometry(positions, "subtract");
}

function* rollbackEditFeatureChanges({ payload: withSave }: ReturnType<typeof resetEditFeatureChanges>) {
  const currentNewFeature: FeatureDcExtended = yield select(getFeatureToShow);
  const currentSelectFeature: FeatureDcExtended = yield select(getCurrentFeature);
  const currentFeature = currentSelectFeature || currentNewFeature;
  const lastFeatureGeometry: EditFeatureState = yield select(getFirstPastEditFeature);

  if (!lastFeatureGeometry || !currentFeature) return;

  if (!withSave) {
    yield call(() =>
      apiUpdateFeature(currentFeature.layer, {
        ...currentFeature,
        geometry: {
          ...currentFeature.geometry,
          coordinates: lastFeatureGeometry.geometry as PositionDc,
        },
      }),
    );
    yield put(setGeometryEditor({ geometry: [...(lastFeatureGeometry.geometry || [])] }));
  }

  yield put(resetChangeHistory());
}

function* updateAttributesInNewFeature({ payload }: ReturnType<typeof updateAttributesForNewFeature>) {
  yield call(waitAction, setAttributes);

  const { attributes } = yield select(getCurrentEditFeature);

  const updatedAttributes = attributes.map(attribute =>
    payload[attribute.name] !== void 0 ? { ...attribute, value: payload[attribute.name] } : attribute,
  );

  yield put(setAttributes(updatedAttributes));
}

function* editFeatureSagas() {
  yield takeLatest(resetEditFeatureChanges, rollbackEditFeatureChanges);
  yield takeLatest(unitEditFeatureGeometry, unitGeometry);
  yield takeLatest(subtractEditFeatureGeometry, subtractGeometry);
  yield takeLatest(updateAttributesForNewFeature, updateAttributesInNewFeature);
}

export default editFeatureSagas;
